import { allowFileTypes } from "@/constants/constants";
import {
  BatchDirectoryStructure,
  BatchFileItem,
  BatchFolderItem,
} from "@/interfaces/project.interface";

export const useNestedStructure = (): {
  createNestedDirectoryStructure: (
    folders: BatchFolderItem[],
    files: BatchFileItem[],
  ) => {
    structure: BatchDirectoryStructure | null;
    validFiles: BatchFileItem[];
    hasUnsupportedFiles: boolean;
    unsupportedFileNames: string[];
  };
} => {
  /**
   * Creates a nested directory structure following the BatchDirectoryStructure interface
   * @param {BatchFolderItem[]} folders - Array of BatchFolderItem objects representing folders to be structured
   * @param {BatchFileItem[]} files - Array of BatchFileItem objects representing files to be added to the structure
   * @returns {BatchDirectoryStructure} BatchDirectoryStructure object containing organized directories and files
   */
  const createNestedDirectoryStructure = (
    folders: BatchFolderItem[],
    files: BatchFileItem[],
  ): {
    structure: BatchDirectoryStructure | null;
    validFiles: BatchFileItem[];
    hasUnsupportedFiles: boolean;
    unsupportedFileNames: string[];
  } => {
    // Parse allowed file extensions from the constant
    const allowedExtensions = allowFileTypes
      .split(",")
      .map((type) => type.trim().replace(".", "").toLowerCase());

    // Filter files based on extension
    const validFiles: BatchFileItem[] = [];
    const unsupportedFiles: BatchFileItem[] = [];
    const unsupportedFileNames: string[] = [];

    // Check each file against allowed extensions
    files.forEach((fileItem) => {
      const fileName = fileItem.file.name;
      const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

      if (allowedExtensions.includes(fileExtension)) {
        // If valid, add to validFiles
        validFiles.push(fileItem);
      } else {
        unsupportedFiles.push(fileItem);
        unsupportedFileNames.push(fileName);
      }
    });

    // Notify user about unsupported files
    if (unsupportedFiles.length > 0) {
      // If all files are unsupported, return null structure
      if (validFiles.length === 0) {
        return {
          structure: null,
          validFiles: [],
          hasUnsupportedFiles: true,
          unsupportedFileNames,
        };
      }
    }
    // Create a map of folder paths to folder objects for easy lookup
    const folderMap = new Map<
      string,
      {
        name: string;
        path: string[];
        documents: {
          name: string;
          size: number;
          path: string[];
          file: File;
        }[];
      }
    >();
    // First, create formatted folders with empty documents arrays
    const formattedFolders = folders.map((folderItem) => {
      const folder = {
        name: folderItem.name,
        path: folderItem.path || [],
        documents: [], // Initialize with empty documents array
      };

      // Add to map for later file assignment
      const folderKey = [...folder.path, folder.name].join("/");
      folderMap.set(folderKey, folder);

      return folder;
    });

    // Find which folder each file belongs to and add to its documents array
    validFiles.forEach((fileItem) => {
      const filePath = fileItem.path || [];
      const folderKey = filePath.join("/");

      // Format file for API
      const formattedFile = {
        name: fileItem.file.name,
        size: fileItem.file.size,
        path: filePath,
        file: fileItem.file, // Keep reference to original file
      };

      // If this file belongs to a folder, add to that folder's documents
      if (folderMap.has(folderKey)) {
        const folder = folderMap.get(folderKey);
        folder?.documents.push(formattedFile);
      }
    });

    // Format files separately for the overall structure (as required by API)
    const formattedFiles = files.map((fileItem) => ({
      name: fileItem.file.name,
      size: fileItem.file.size,
      path: fileItem.path || [],
    }));

    return {
      structure: {
        directories: formattedFolders,
        files: formattedFiles,
      },
      validFiles,
      hasUnsupportedFiles: unsupportedFiles.length > 0,
      unsupportedFileNames,
    };
  };

  return { createNestedDirectoryStructure };
};
